import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import SectionProjectsCard from '../sections/SectionProjectsCard';

const WORK_URL = `${process.env.PUBLIC_URL || ''}/projects/index.json`;

/**
 * Work landing page (#work) – SLS-style: intro, process, capability sections, portfolio grid.
 */
export default function WorkPage({ onBack, onContact }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(WORK_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items = Array.isArray(data?.items) ? data.items : [];
  const process = Array.isArray(data?.process) ? data.process : [];
  const sections = Array.isArray(data?.sections) ? data.sections : [];
  const capabilities = Array.isArray(data?.capabilities) ? data.capabilities : [];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--jb-forest-bg)' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 0%, var(--jb-glow-cyan) 0%, transparent 70%), radial-gradient(ellipse 40% 30% at 90% 80%, var(--jb-glow-green) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-8 pt-24 pb-20">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan mb-10 transition text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        {/* Hero */}
        <header className="mb-14 max-w-3xl">
          {(data?.eyebrow || 'Delivery') && (
            <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-3">
              {data?.eyebrow ?? 'Delivery'}
            </p>
          )}
          <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-text-hero mb-5 leading-tight tracking-tight">
            {data?.title ?? data?.heading ?? 'Work'}
          </h1>
          {(data?.intro || data?.subtext) && (
            <p className="font-body text-lg text-text-body leading-relaxed mb-8">
              {data?.intro ?? data?.subtext}
            </p>
          )}
          <button
            type="button"
            onClick={onContact}
            className="jb-cta inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition"
          >
            {data?.ctaText ?? 'Book a discovery call'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </header>

        {/* Partnership process */}
        {(data?.partnershipHeading || process.length > 0) && (
          <section className="mb-16">
            {data?.partnershipHeading && (
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-text-hero mb-3">
                {data.partnershipHeading}
              </h2>
            )}
            {data?.partnershipIntro && (
              <p className="font-body text-text-sub max-w-3xl mb-10 leading-relaxed">
                {data.partnershipIntro}
              </p>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {process.map((step) => (
                <div
                  key={step.step}
                  className="p-5 rounded-xl border bg-forest-card"
                  style={{ borderColor: 'var(--jb-border)' }}
                >
                  <div className="font-mono text-cyan text-sm font-bold mb-3">{step.step}</div>
                  <h3 className="font-display font-bold text-text-hero mb-2">{step.title}</h3>
                  <p className="font-body text-sm text-text-sub leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Content sections */}
        {sections.length > 0 && (
          <section className="mb-16 space-y-10 max-w-3xl">
            {sections.map((sec, i) => (
              <div key={i}>
                <h2 className="font-display font-extrabold text-xl sm:text-2xl text-text-hero mb-3">
                  {sec.heading}
                </h2>
                <p className="font-body text-text-sub leading-relaxed">{sec.body}</p>
              </div>
            ))}
          </section>
        )}

        {/* Capability cards */}
        {capabilities.length > 0 && (
          <section className="mb-16">
            <div className="grid sm:grid-cols-2 gap-4">
              {capabilities.map((cap, i) => (
                <div
                  key={i}
                  className="p-5 rounded-xl border bg-forest-card"
                  style={{ borderColor: 'var(--jb-border)' }}
                >
                  <h3 className="font-display font-bold text-text-hero mb-1">{cap.title}</h3>
                  <p className="font-body text-sm text-text-sub leading-relaxed">{cap.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Portfolio */}
        <section className="mb-16">
          <div className="mb-8">
            <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-2">Portfolio</p>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-text-hero mb-2">
              {data?.portfolioHeading ?? 'Selected engagements'}
            </h2>
            {(data?.portfolioSubtext || data?.subtext) && (
              <p className="font-body text-text-sub max-w-xl">
                {data?.portfolioSubtext ?? data?.subtext}
              </p>
            )}
          </div>

          {loading ? (
            <p className="font-body text-text-sub">Loading…</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {items.map((ref) => (
                <SectionProjectsCard
                  key={ref.id || ref.slug || ref.title}
                  title={ref.title ?? ''}
                  description={ref.description ?? ''}
                  tags={Array.isArray(ref.tags) ? ref.tags : []}
                  badge={ref.badge}
                  client={ref.client}
                  href={ref.slug && ref.contentPath ? `#project/${ref.slug}` : (ref.href ?? '#')}
                />
              ))}
            </div>
          )}
        </section>

        {/* Closing CTA */}
        <section
          className="p-8 sm:p-10 rounded-2xl border text-center"
          style={{ background: 'var(--jb-connect-bg)', borderColor: 'var(--jb-connect-border)' }}
        >
          <h2 className="font-display font-black text-2xl sm:text-3xl text-text-hero mb-3">
            {data?.closingHeading ?? "Let's talk about your challenge"}
          </h2>
          <p className="font-body text-text-sub max-w-xl mx-auto mb-6">
            {data?.closingSubtext ?? 'Tell us where you are headed. We will help you lay the bricks.'}
          </p>
          <button
            type="button"
            onClick={onContact}
            className="jb-cta inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition"
          >
            {data?.ctaText ?? 'Book a discovery call'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </section>
      </div>
    </div>
  );
}
